import { useScrollbar } from "@/mono/third-party/r3f-scroll-rig-custom";
import { useTracker } from "mono/third-party/r3f-scroll-rig-custom";
import { useEffect, useMemo, useRef } from "react";
import { lerp } from "../math";
import { isMobileLayout } from "../responsive";
import { useTicker } from "./useRaf";

// TODO: Find way to not trigger stuff thats in viewport on initial load
export function useScrollRigScrollTrigger({
  el,
  handleIn,
  handleOut,
  offset: _offset,
  update,
  lerpAlpha = 0.1,

  debug = false,
  deps = [],
}) {
  const offset = useMemo(
    () => (_offset !== undefined ? _offset : isMobileLayout() ? 0.5 : 0),
    [_offset],
  );

  const element = useMemo(() => {
    if (!el) return { current: null };
    return el;
  }, [el]);

  const { onScroll } = useScrollbar();
  const tracker = useTracker(element, {
    threshold: Math.abs(offset),
  });

  const unsubRef = useRef();
  const visible = useRef(false);
  const progress = useRef(0);
  const _v = useRef(0);

  const _scrollState = useRef(null);

  const { isActive, start, stop } = useTicker(({ delta }) => {
    if (!update) return;

    // Only update if the change is significant enough
    const diff = Math.abs(_v.current - progress.current);
    if (diff > 0.001) {
      update?.(_v.current, _scrollState.current);
      _v.current = lerp(_v.current, progress.current, lerpAlpha, delta);
    } else {
      // If we're close enough to target, snap and stop the animation
      _v.current = progress.current;
      stop();
    }
  });

  function unsubscribe() {
    unsubRef.current?.();
    if (unsubRef.current) unsubRef.current = null;
  }

  useEffect(() => {
    if (!el) return;
    if (unsubRef.current) return;

    unsubRef.current = onScroll(() => {
      const { scrollState } = tracker;

      if (update) {
        _scrollState.current = scrollState;
        progress.current = scrollState.viewport;

        if (scrollState.visibility > 0 && !isActive()) {
          start();
        }
        if (scrollState.visibility < 0 && isActive()) {
          stop();
        }
      }

      if (debug) console.log("VISIBLE", visible.current);

      if (scrollState.inViewport && !visible.current) {
        handleIn?.();
        visible.current = true;
        if (!handleOut && !update) {
          unsubscribe();
        }
      }

      if (handleOut && visible.current && !scrollState.inViewport) {
        visible.current = false;
        handleOut?.();
      }
    });

    return () => {
      unsubscribe();
    };
  }, [tracker, onScroll, _offset, ...deps]);

  return {
    isVisible: () => visible.current,
    setVisible: (v) => (visible.current = v),
  };
}
